import React, { useState, useRef } from "react";
import axios from "axios";

const MAX_QUESTIONS = 3;

function App() {
  const [resume, setResume] = useState("");
  const [messages, setMessages] = useState([]);
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [questionCount, setQuestionCount] = useState(0);
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const recognitionRef = useRef(null);

  // ===========================
  // TEXT TO SPEECH
  // ===========================
  const speak = (text) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1;
    utterance.pitch = 1;
    window.speechSynthesis.speak(utterance);
  };

  // ===========================
  // SPEECH TO TEXT
  // ===========================
  const startListening = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      handleAnswer(transcript);
    };

    recognition.start();
    recognitionRef.current = recognition;
  };

  // ===========================
  // GEMINI CALL
  // ===========================
  const callGemini = async (promptText, attempt = 1) => {
  try {
    const response = await axios.post("/api/interview/ask", { prompt: promptText });
    const result = response?.data?.response ?? "Error generating response.";

    // Check if response is quota-related
    const quotaMatch = result.match(/retry after (\d+(\.\d+)?)s/i);
    if (quotaMatch && attempt <= 3) {
      const retrySeconds = parseFloat(quotaMatch[1]);
      console.warn(`Quota exceeded. Retrying in ${retrySeconds}s...`);

      // Wait for suggested retry delay
      await new Promise(res => setTimeout(res, retrySeconds * 1000));

      // Retry recursively
      return await callGemini(promptText, attempt + 1);
    }

    return result;
  } catch (error) {
    console.error("Error calling Gemini:", error);
    return "Server error. Please try again later.";
  }
};

  // ===========================
  // START INTERVIEW
  // ===========================
  const startInterview = async () => {
    if (!resume) return alert("Please paste resume");

    setLoading(true);
    setResult("");
    setMessages([]);
    setQuestionCount(1);

    const prompt = `
You are a senior technical interviewer.

Candidate Resume:
${resume}

Ask ONLY one technical question.
Do NOT evaluate.
`;

    const firstQuestion = await callGemini(prompt);

    setMessages([{ role: "assistant", text: firstQuestion }]);
    setInterviewStarted(true);

    speak(firstQuestion);

    setLoading(false);
  };

  // ===========================
  // HANDLE ANSWER
  // ===========================
  const handleAnswer = async (answer) => {
    const updatedMessages = [
      ...messages,
      { role: "user", text: answer }
    ];

    setMessages(updatedMessages);
    setLoading(true);

    if (questionCount >= MAX_QUESTIONS) {
      await evaluateCandidate(updatedMessages);
      setLoading(false);
      return;
    }

    const conversationText = updatedMessages
      .map(m => `${m.role}: ${m.text}`)
      .join("\n");

    const prompt = `
You are conducting a technical interview.

Conversation:
${conversationText}

Ask the NEXT technical question only.
Do NOT evaluate.
`;

    const nextQuestion = await callGemini(prompt);

    setMessages([
      ...updatedMessages,
      { role: "assistant", text: nextQuestion }
    ]);

    setQuestionCount(prev => prev + 1);

    speak(nextQuestion);

    setLoading(false);
  };

  // ===========================
  // EVALUATION
  // ===========================
  const evaluateCandidate = async (conversation) => {
    const conversationText = conversation
      .map(m => `${m.role}: ${m.text}`)
      .join("\n");

    const prompt = `
Evaluate this candidate interview.

Conversation:
${conversationText}

Provide:
- Score out of 10
- Strengths
- Weaknesses
- Hiring Recommendation
`;

    const evaluation = await callGemini(prompt);

    setResult(evaluation);
    speak("Interview completed. Here is your evaluation.");
    setInterviewStarted(false);
  };

  return (
    <div style={{ padding: 40, maxWidth: 800, margin: "auto" }}>
      <h2>🎙 Voice AI Interviewer</h2>

      {!interviewStarted && !result && (
        <>
          <textarea
            rows="6"
            style={{ width: "100%" }}
            placeholder="Tell us your skills or paste your resume here…"
            value={resume}
            onChange={(e) => setResume(e.target.value)}
          />
          <br /><br />
          <button onClick={startInterview} disabled={loading}>
            {loading ? "Starting..." : "Start Voice Interview"}
          </button>
        </>
      )}

      {interviewStarted && (
        <>
          <h3>Interview In Progress...</h3>

          {messages.map((m, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <b>{m.role === "assistant" ? "Interviewer" : "You"}:</b>
              <div>{m.text}</div>
            </div>
          ))}

          <br />

          <button onClick={startListening} disabled={loading}>
            🎤 Speak Answer
          </button>
        </>
      )}

      {result && (
        <>
          <h3>Final Evaluation</h3>
          <pre style={{ whiteSpace: "pre-wrap" }}>{result}</pre>
          <button onClick={() => window.location.reload()}>
            Start New Interview
          </button>
        </>
      )}
    </div>
  );
}

export default App;